import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaMoon, FaSun, FaLock, FaEye, FaEyeSlash } from "react-icons/fa"; 
import api from "../api";
import { ACCESS_TOKEN, REFRESH_TOKEN } from "../constants";
import "../styles/Login.css";
import { isPaid } from "../utils/payments";

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false); // 👁️‍🗨️ nuevo
  const [dark, setDark] = useState(localStorage.getItem("theme") === "dark");
  const [needPayment, setNeedPayment] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search || '');
      setNeedPayment(params.get('needPayment') === '1');
    } catch {}
  }, [location.search]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      // El backend espera "username" por SimpleJWT. Enviamos el correo en ese campo.
      const normalizedEmail = String(email || "").trim().toLowerCase();
      const normalizedPassword = String(password || "");
      const res = await api.post("token/", { username: normalizedEmail, password: normalizedPassword });
      if (res.data?.access && res.data?.refresh) {
        localStorage.setItem(ACCESS_TOKEN, res.data.access);
        localStorage.setItem(REFRESH_TOKEN, res.data.refresh);
        // Guardamos el correo como "username" para mantener compatibilidad con componentes existentes
        localStorage.setItem("username", normalizedEmail);
        navigate("/resumen");
      } else {
        alert("Respuesta inesperada del servidor");
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 400) {
        const detail = err.response?.data?.detail || err.response?.data?.error || "Credenciales incorrectas";
        alert(String(detail));
      } else if (err.response) {
        const detail = err.response?.data?.detail || err.response?.data?.error || err.response.statusText || "Error desconocido";
        alert(`Error ${err.response.status}: ${detail}`);
      } else if (err.request) {
        alert("No hay respuesta del servidor. Verifica la red / CORS.");
      } else {
        alert("Error al preparar la petición");
      }
    }
  };

  return (
    <div
      className={`min-h-screen flex items-center justify-center transition-colors duration-700
      bg-gray-100 dark:bg-gray-900`}
    >
      <div
        className="bg-white dark:bg-gray-800 shadow-2xl rounded-2xl p-8 sm:p-10 w-full max-w-md border border-gray-200 dark:border-gray-700 
        transform transition-all duration-500 hover:scale-[1.01]"
      >
        {/* Encabezado */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
            Bienvenido 👋
          </h2>

          <button
            onClick={() => setDark(!dark)}
            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition"
            title="Alternar tema"
          >
            {dark ? <FaSun size={16} /> : <FaMoon size={16} />}
          </button>
        </div>

        {/* Logo central (clic para ir a la landing) */}
        <div className="flex justify-center mb-6">
          <img
            src="/images/logowave.png"
            alt="WaveSales"
            className="w-28 h-28 object-contain rounded-full cursor-pointer hover:opacity-90 transition"
            onClick={() => navigate('/')}
            title="Ir a WaveSales"
          />
        </div>

        {/* Aviso de pago requerido */}
        {needPayment && (
          <div className="mb-4 text-sm text-amber-700 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200 dark:border-amber-800 rounded p-3">
            Para registrarte, primero realiza el pago en la <button className="underline" onClick={() => navigate('/#pricing')}>sección de Precios</button>.
          </div>
        )}

        {/* Formulario */}
        <form onSubmit={handleLogin} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
              Correo electrónico
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
              bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 
              focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors duration-300"
              placeholder="tu@correo.com"
              autoComplete="email"
              inputMode="email"
            />
          </div>

          {/* Contraseña con ojito 👁️ */}
          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
              Contraseña
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-2 pr-10 rounded-lg border border-gray-300 dark:border-gray-600 
                bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 
                focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors duration-300"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-2.5 text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400 transition"
                title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <div className="flex justify-between items-center text-sm">
            <label className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
              <input type="checkbox" className="accent-indigo-500" /> Recordarme
            </label>
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div>

          <button
            type="submit"
            className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 
            text-white font-semibold py-2.5 rounded-lg shadow-md shadow-indigo-200/40 dark:shadow-none 
            transition-transform active:scale-95"
          >
            Iniciar sesión
          </button>
        </form>

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
          ¿No tienes una cuenta?{" "}
          {isPaid() ? (
            <button
              onClick={() => navigate("/register")}
              className="text-indigo-600 dark:text-indigo-400 font-medium hover:underline"
            >
              Regístrate
            </button>
          ) : (
            <button
              onClick={() => navigate('/#pricing')}
              className="text-gray-400 dark:text-gray-500 cursor-not-allowed"
              title="Completa el pago en la landing para registrarte"
            >
              Regístrate (requiere pago)
            </button>
          )}
        </div>

        {/* Footer de diagnóstico eliminado a solicitud (ocultar API base) */}
      </div>
    </div>
  );
}

export default Login;
